import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { useStoreInfo } from '@/lib/store';
import { Button } from '@/components/ui/button';

export function ContactPage() {
  const { storeInfo } = useStoreInfo();
  const pageTitle = `Contact Us${storeInfo?.site_name ? ` - ${storeInfo.site_name}` : ''}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="Contact us for all your needs" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-brand-primary py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
       
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display">
              Contact Us
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Get in touch with our specialists
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6 font-display text-gray-900">
                Send us a Message
              </h2>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 rounded-full border-gray-300 focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      className="w-full px-4 py-3 rounded-full border-gray-300 focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-full border-gray-300 focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    placeholder="Enter your subject"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-4 py-3 rounded-2xl border-gray-300 focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    placeholder="Your message"
                  />
                </div>
                <Button type="submit" className="w-full">
                  Send Message
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-6 font-display text-gray-900">
                  Contact Information
                </h2>
                <div className="space-y-6">
                  {storeInfo?.contact_email && (
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-brand-50 rounded-full">
                          <Mail className="h-6 w-6 text-brand-primary" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <a
                          href={`mailto:${storeInfo.contact_email}`}
                          className="text-lg font-medium text-gray-900 hover:text-brand-primary"
                        >
                          {storeInfo.contact_email}
                        </a>
                      </div>
                    </div>
                  )}

                  {storeInfo?.phone_num && (
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-brand-50 rounded-full">
                          <Phone className="h-6 w-6 text-brand-primary" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Phone</p>
                        <a
                          href={`tel:${storeInfo.phone_num}`}
                          className="text-lg font-medium text-gray-900 hover:text-brand-primary"
                        >
                          {storeInfo.phone_num}
                        </a>
                      </div>
                    </div>
                  )}

                  {storeInfo?.shop_address && (
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-brand-50 rounded-full">
                          <MapPin className="h-6 w-6 text-brand-primary" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Address</p>
                        <p className="text-lg font-medium text-gray-900">
                          {storeInfo.shop_address}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="p-3 bg-brand-50 rounded-full">
                        <Clock className="h-6 w-6 text-brand-primary" />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Business Hours</p>
                      <div className="text-lg font-medium text-gray-900">
                        <p>Mon, Wed, Fri, Sat: 9:00 AM - 5:00 PM</p>
                        <p>Tue, Thu: 2:00 PM - 5:00 PM</p>
                        <p>Sun: Closed</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {storeInfo?.google_map && (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div 
                    dangerouslySetInnerHTML={{ __html: storeInfo.google_map }}
                    className="w-full h-[300px]"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

